"use client";

import { Alignment, Fit, Layout, useRive } from "@rive-app/react-webgl2";
import type { Rive } from "@rive-app/react-webgl2";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, RefObject } from "react";

import {
  OUR_CUSTOMERS_HERO_SQUARE_VM_NAMES,
  SQUARE_VM_NAME,
  SQUARE_VM_STRING_PROPERTY,
} from "@/components/customers/hero-rive-customer-names";
import {
  RiveSafetyBoundary,
  useRiveCleanup,
  useRiveLoadFailure,
  useRiveWebGLContextGuard,
} from "@/components/rive/rive-safety-boundary";
import { cn } from "@/lib/utils";

const RIVE_SRC = "/assets-next/our-customers/our-customers-hero-desktop.riv";
const ARTBOARD = "CustomerHero";
/** Runtime-exported state machine name (not the editor default "State Machine 1"). */
const STATE_MACHINE = "CustomHero";
const AMBIENT_ANIMATION = "Timeline 1";
const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)";

/**
 * Apply Figma-aligned customer names to SquareVM instances.
 *
 * Editor-bound instances (indices ≥ 1) are read-only at runtime; their values
 * are activated via `autoBind: true`. We still attempt overrides when the
 * runtime exposes a writable `stringProperty` (e.g. freshly created instances).
 */
function bindHeroCustomerNames(rive: Rive) {
  const squareVm = rive.viewModelByName(SQUARE_VM_NAME);
  if (!squareVm) return;

  for (const [indexRaw, name] of Object.entries(
    OUR_CUSTOMERS_HERO_SQUARE_VM_NAMES
  )) {
    const index = Number(indexRaw);
    if (
      !Number.isInteger(index) ||
      index < 0 ||
      index >= squareVm.instanceCount
    ) {
      continue;
    }

    const instance = squareVm.instanceByIndex(index);
    const stringProp = instance?.string(SQUARE_VM_STRING_PROPERTY);
    if (stringProp) {
      stringProp.value = name;
    }
  }
}

function resolveHeroPlaybackTarget(rive: Rive): string | null {
  const stateMachines = rive.stateMachineNames ?? [];
  const animations = rive.animationNames ?? [];

  if (stateMachines.includes(STATE_MACHINE)) {
    return STATE_MACHINE;
  }

  if (animations.includes(AMBIENT_ANIMATION)) {
    return AMBIENT_ANIMATION;
  }

  return animations[0] ?? null;
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  return prefersReducedMotion;
}

/** Pause WebGL while the hero is off-screen or the page is actively scrolling. */
function useHeroRivePlaybackControl(
  rive: Rive | null,
  containerRef: RefObject<HTMLElement | null>,
  playbackTarget: string | null
) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isVisible, setIsVisible] = useState(true);
  const [isScrolling, setIsScrolling] = useState(false);
  const playbackTargetRef = useRef(playbackTarget);

  useEffect(() => {
    playbackTargetRef.current = playbackTarget;
  }, [playbackTarget]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry?.isIntersecting ?? false);
      },
      { threshold: 0, rootMargin: "64px 0px" }
    );

    observer.observe(container);
    return () => observer.disconnect();
  }, [containerRef]);

  useEffect(() => {
    let scrollEndTimer: ReturnType<typeof setTimeout> | undefined;

    const onScroll = () => {
      setIsScrolling(true);
      if (scrollEndTimer) clearTimeout(scrollEndTimer);
      scrollEndTimer = setTimeout(() => {
        setIsScrolling(false);
      }, 120);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (scrollEndTimer) clearTimeout(scrollEndTimer);
    };
  }, []);

  useEffect(() => {
    if (!rive || !playbackTargetRef.current) return;

    const shouldPlay = isVisible && !isScrolling && !prefersReducedMotion;

    if (shouldPlay) {
      rive.play(playbackTargetRef.current);
      return;
    }

    rive.pause();
  }, [rive, isVisible, isScrolling, prefersReducedMotion]);
}

interface OurCustomersHeroRiveBackgroundProps {
  /** Desktop shows the full 1440×562 frame; mobile crops to the right section. */
  variant: "desktop" | "mobile";
  className?: string;
  style?: CSSProperties;
  onReady?: () => void;
}

function RiveFallback({
  className,
  style,
}: Pick<OurCustomersHeroRiveBackgroundProps, "className" | "style">) {
  return (
    <div
      aria-hidden
      data-testid="our-customers-hero-rive-fallback"
      className={cn("absolute inset-0 overflow-hidden bg-[#0c0d0d]", className)}
      style={style}
    />
  );
}

function useIsActiveRiveVariant(variant: "desktop" | "mobile") {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_MEDIA_QUERY);
    const update = () => {
      setIsActive(
        variant === "desktop" ? mediaQuery.matches : !mediaQuery.matches
      );
    };

    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, [variant]);

  return isActive;
}

export function OurCustomersHeroRiveBackground({
  onReady,
  ...props
}: OurCustomersHeroRiveBackgroundProps) {
  const isActive = useIsActiveRiveVariant(props.variant);
  const fallback = <RiveFallback {...props} />;

  if (!isActive) {
    return fallback;
  }

  return (
    <RiveSafetyBoundary fallback={fallback} onError={onReady}>
      <OurCustomersHeroRiveCanvas {...props} onReady={onReady} />
    </RiveSafetyBoundary>
  );
}

function OurCustomersHeroRiveCanvas({
  variant,
  className,
  style,
  onReady,
}: OurCustomersHeroRiveBackgroundProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [playbackTarget, setPlaybackTarget] = useState<string | null>(null);
  const { loadFailed, handleLoadError, handleContextLost } =
    useRiveLoadFailure(onReady);
  const layout = useMemo(
    () =>
      new Layout({
        fit: Fit.Cover,
        alignment:
          variant === "mobile" ? Alignment.CenterRight : Alignment.Center,
      }),
    [variant]
  );
  const handleRiveReady = useCallback(
    (instance: Rive) => {
      bindHeroCustomerNames(instance);
      setPlaybackTarget(resolveHeroPlaybackTarget(instance));
      onReady?.();
    },
    [onReady]
  );
  const { RiveComponent, rive, canvas } = useRive(
    {
      src: RIVE_SRC,
      artboard: ARTBOARD,
      stateMachines: STATE_MACHINE,
      autoplay: false,
      // Activates SquareVM instance bindings so embedded customer names render
      // instead of the SquareTexts placeholder "text".
      autoBind: true,
      onRiveReady: handleRiveReady,
      onLoadError: handleLoadError,
      layout,
    },
    { shouldResizeCanvasToContainer: true }
  );

  useRiveWebGLContextGuard(canvas, rive, handleContextLost);
  useRiveCleanup(rive);
  useHeroRivePlaybackControl(rive, containerRef, playbackTarget);

  if (loadFailed) {
    return <RiveFallback className={className} style={style} />;
  }

  return (
    <div
      ref={containerRef}
      aria-hidden
      data-testid={
        variant === "desktop"
          ? "our-customers-hero-rive-desktop"
          : "our-customers-hero-rive-mobile"
      }
      className={cn("absolute inset-0 overflow-hidden bg-[#0c0d0d]", className)}
      style={style}
    >
      <RiveComponent className="absolute inset-0 size-full [&_canvas]:absolute [&_canvas]:inset-0 [&_canvas]:size-full" />
    </div>
  );
}
